// frontend/src/store/auth.js

import { reactive, watch } from 'vue';
import { roleState } from './roles';
import { getUserFromToken } from '@/utils/tokenUtils';
import { setAccessToken, clearAccessToken, getAccessToken, migrateFromLocalStorageOnce } from '@/services/tokenStore';
import { resetAccessTokenRefresh } from '@/services/axios';
import { accessState } from './access';

// Инициализируем состояние авторизации
const initAuthState = () => {
  // Мигрируем токен из localStorage один раз при инициализации стора
  if (getAccessToken() == null) {
    migrateFromLocalStorageOnce();
  }
  const token = getAccessToken();
  const isAuthenticated = !!token && token !== 'null';

  let role = 'user';
  if (isAuthenticated) {
    const userData = getUserFromToken();
    // Keep the saved role as a bootstrap fallback until the profile is loaded.
    const savedRole = localStorage.getItem('user_role');
    role = userData?.role || savedRole || 'user';
  }

  return {
    isAuthenticated,
    role,
    isInitialized: false // Флаг инициализации
  };
};

export const authState = reactive(initAuthState());

// Функция для нормализации старого формата ролей
function normalizeLegacyRole(role) {
  // Приводим роли к нижнему регистру для единообразия
  const normalizedRole = typeof role === 'string' ? role.toLowerCase() : role;

  // Маппинг старых названий ролей на новые
  if (normalizedRole === 'admin') return 'admin';
  if (normalizedRole === 'atameken_admin' || normalizedRole === 'atameken_admin') return 'atameken_admin';
  return 'user';
}

export function login(token, role, options = {}) {
  // Проверяем, что токен имеет правильный формат JWT
  if (!token || typeof token !== 'string') {
    console.error('Неверный токен для сохранения:', token);
    return;
  }

  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    console.error('Неверный формат JWT токена:', token);
    return;
  }

  resetAccessTokenRefresh();
  setAccessToken(token);

  // Получаем данные из токена
  const userData = getUserFromToken();
  const tokenRole = userData?.role || role;

  // Нормализуем роль и сохраняем в localStorage.
  const normalizedRole = normalizeLegacyRole(tokenRole);
  localStorage.setItem('user_role', normalizedRole);

  // Обновляем состояние авторизации
  authState.isAuthenticated = true;
  authState.role = normalizedRole;

  // Обновляем состояние в новой системе ролей
  roleState.setRole(normalizedRole);

  // Завершаем инициализацию если она еще не завершена
  if (options.finishInitialization !== false && !authState.isInitialized) {
    authState.isInitialized = true;
  }
}

export function logout() {
  resetAccessTokenRefresh();
  clearAccessToken();

  // Очищаем пользовательские настройки и кэши
  try {
    localStorage.removeItem('user_role');
    localStorage.removeItem('user-language');
    localStorage.removeItem('user-country');
    localStorage.removeItem('smartSearchState');
    localStorage.removeItem('redirectUrl');
  } catch (e) {
    // ignore
  }

  // Обновляем состояние авторизации
  authState.isAuthenticated = false;
  authState.role = 'user';

  // Обновляем состояние в новой системе ролей
  roleState.setRole('user');
  accessState.clear();

  // Сбрасываем флаг инициализации
  authState.isInitialized = false;
}

// Функция для завершения инициализации аутентификации
export function finishAuthInitialization() {
  authState.isInitialized = true;
}

// Промис, который разрешается, когда восстановление сессии на старте
// завершилось. Публичным страницам, которым важно, вошёл ли пользователь,
// нужно ждать его: иначе запрос уходит раньше, чем вернётся токен, или
// падает вместе с неудачным refresh у гостя.
export function whenAuthReady() {
  if (authState.isInitialized) return Promise.resolve();
  return new Promise((resolve) => {
    const stop = watch(
      () => authState.isInitialized,
      (ready) => {
        if (!ready) return;
        stop();
        resolve();
      },
    );
  });
}

// Функция для обновления роли пользователя (вызывается после загрузки данных с сервера)
export function updateRole(role) {
  const normalizedRole = normalizeLegacyRole(role);
  authState.role = normalizedRole;
  localStorage.setItem('user_role', normalizedRole);
  roleState.setRole(normalizedRole);
}
